import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

export default function AuthModal() {
  const { authModalOpen, closeAuth, login, register, googleLogin } = useAuth();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const forgot = async () => {
    if (!email.trim()) { setError("Enter your email above first, then click Forgot password."); return; }
    setError(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: email.trim() });
      setNotice(data.message);
    } catch (e) { setError(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const reset = () => {
    setError(""); setBusy(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const res =
      mode === "login"
        ? await login(email, password)
        : await register(name, email, password);
    setBusy(false);
    if (res.ok) {
      toast.success(mode === "login" ? "Welcome back." : "Your journey begins.");
      closeAuth();
      setName(""); setEmail(""); setPassword("");
    } else {
      setError(res.error);
    }
  };

  return (
    <Dialog open={authModalOpen} onOpenChange={(o) => { if (!o) closeAuth(); reset(); }}>
      <DialogContent className="max-w-md overflow-hidden p-0" data-testid="auth-modal">
        <div className="bg-secondary px-6 py-7 text-secondary-foreground">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-white/70">
            {mode === "login" ? "Welcome back" : "Join the conversation"}
          </p>
          <DialogHeader className="mt-2 space-y-1 text-left">
            <DialogTitle className="font-serif text-3xl font-bold text-white">
              {mode === "login" ? "Continue asking." : "Curiosity breeds intimacy."}
            </DialogTitle>
            <DialogDescription className="text-white/70">
              Save questions, keep a private journal, and track your study.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="px-6 py-6">
          <Button
            type="button"
            data-testid="google-auth-button"
            variant="outline"
            onClick={googleLogin}
            className="w-full gap-2 rounded-full border-border"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
            </svg>
            Continue with Google
          </Button>

          <div className="my-4 flex items-center gap-3 text-xs text-muted-foreground">
            <span className="h-px flex-1 bg-border" /> or <span className="h-px flex-1 bg-border" />
          </div>

          <form onSubmit={submit} className="space-y-3">
            {mode === "register" && (
              <div className="space-y-1.5">
                <Label htmlFor="auth-name">Name</Label>
                <Input
                  id="auth-name"
                  data-testid="auth-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  required
                />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="auth-email">Email</Label>
              <Input
                id="auth-email"
                data-testid="auth-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
              />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="auth-password">Password</Label>
                {mode === "login" && (
                  <button type="button" data-testid="auth-forgot-button" onClick={forgot} disabled={busy} className="text-xs font-semibold text-primary hover:underline disabled:opacity-60">Forgot password?</button>
                )}
              </div>
              <Input
                id="auth-password"
                data-testid="auth-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={mode === "register" ? "At least 6 characters" : "\u2022\u2022\u2022\u2022\u2022\u2022"}
                required
              />
            </div>

            {error && (
              <p data-testid="auth-error" className="text-sm text-destructive">{error}</p>
            )}
            {notice && (
              <p data-testid="auth-notice" className="rounded-xl bg-gold/10 px-3 py-2 text-sm text-foreground">{notice}</p>
            )}

            <Button
              type="submit"
              data-testid="auth-submit-button"
              disabled={busy}
              className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {mode === "login" ? "Sign in" : "Create account"}
            </Button>
          </form>

          <p className="mt-4 text-center text-sm text-muted-foreground">
            {mode === "login" ? "New here?" : "Already have an account?"}{" "}
            <button
              type="button"
              data-testid="auth-mode-toggle"
              onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
              className="font-semibold text-primary hover:underline"
            >
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
